import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Baby, ArrowLeft, Ruler, Info } from 'lucide-react';
import { sizeConversionTable } from '../mock';
import Logo from '../components/Logo';

const SizeGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5]">
      {/* Header */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Logo />
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] bg-clip-text text-transparent">
            Baby Shoe Size Guide
          </h1>
          <p className="text-gray-600 text-lg">Complete size conversion chart for babies aged 0-2 years</p>
        </div>

        {/* Size Conversion Table */}
        <Card className="p-6 mb-8 bg-white/90 backdrop-blur-sm">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Ruler className="w-6 h-6 text-[#A8D5FF]" />
            Size Conversion Chart
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Age</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Foot Length (cm)</th>
                  <th className="text-center py-3 px-4 font-semibold text-[#FFB4A2]">US Size</th>
                  <th className="text-center py-3 px-4 font-semibold text-[#A8D5FF]">EU Size</th>
                  <th className="text-center py-3 px-4 font-semibold text-[#9ADFB4]">UK Size</th>
                </tr>
              </thead>
              <tbody>
                {sizeConversionTable.map((row, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-[#F0F8FF] transition-colors">
                    <td className="py-3 px-4 font-medium text-gray-800">{row.ageMonths} months</td>
                    <td className="py-3 px-4 text-gray-600">{row.lengthCm} cm</td>
                    <td className="py-3 px-4 text-center font-semibold text-[#FFB4A2]">{row.usSize}</td>
                    <td className="py-3 px-4 text-center font-semibold text-[#A8D5FF]">{row.euSize}</td>
                    <td className="py-3 px-4 text-center font-semibold text-[#9ADFB4]">{row.ukSize}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* How to Measure */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6 bg-white/90 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-[#A8D5FF]" />
              How to Measure Manually
            </h3>
            <ol className="space-y-3 text-gray-600">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#FFF5F0]0 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <span>Place baby's foot on a flat surface</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#FFF5F0]0 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <span>Use a ruler or measuring tape</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#FFF5F0]0 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <span>Measure from heel to longest toe</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#FFF5F0]0 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <span>Note the length in centimeters</span>
              </li>
            </ol>
          </Card>

          <Card className="p-6 bg-white/90 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-[#A8D5FF]" />
              Important Tips
            </h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex gap-2">
                <span className="text-[#A8D5FF]">•</span>
                <span>Measure both feet (they may differ slightly)</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#A8D5FF]">•</span>
                <span>Measure in the afternoon when feet are largest</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#A8D5FF]">•</span>
                <span>Add 0.5-1cm for growth room in shoes</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#A8D5FF]">•</span>
                <span>Re-measure every 2-3 months</span>
              </li>
              <li className="flex gap-2">
                <span className="text-[#A8D5FF]">•</span>
                <span>Baby feet grow fast in the first year</span>
              </li>
            </ul>
          </Card>
        </div>

        {/* CTA */}
        <Card className="p-8 bg-gradient-to-br from-pink-400 via-purple-400 to-blue-400 border-0 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Start Measuring?</h2>
          <p className="mb-6 opacity-90">Track your baby's growth with accurate measurements</p>
          <Button
            onClick={() => navigate('/register')}
            className="bg-white text-[#A8D5FF] hover:bg-gray-100"
          >
            Get Started Free
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default SizeGuide;